import { useState } from "react";
import { BookingResponseDto } from "../../api/bookingApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import {
  ArrowRightLeft,
  Ban,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  ClockPlus,
  CreditCard,
  Euro,
  MapPin,
  MessageSquareQuote,
} from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";
import {
  formatSmartDate,
  formatTimeRange,
} from "../dashboard/formatBooking.ts";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useAuth } from "react-oidc-context";
import { TooltipText } from "../TooltipText.tsx";
import ChangeRequestBanner from "./ChangeRequestBanner.tsx";

const VISIBLE_SLOTS = 3;

interface UserBookingCardProps {
  booking: BookingResponseDto;
}

type DisplayStatus =
  | BookingResponseDto["status"]
  | "CANCELLED"
  | "PARTIALLY_CANCELLED";

function getDisplayStatus(booking: BookingResponseDto): DisplayStatus {
  const total = booking.timeSlots.length;
  const cancelled = booking.timeSlots.filter(
    (s) => s.status === "CANCELLED",
  ).length;
  if (total > 0 && cancelled === total) return "CANCELLED";
  if (cancelled > 0) return "PARTIALLY_CANCELLED";
  return booking.status;
}

function getLeftBorder(status: DisplayStatus): string {
  switch (status) {
    case "ACCEPTED":
      return "border-l-soluud-secondary";
    case "BOOKED":
      return "border-l-soluud-primary";
    case "CREATED":
      return "border-l-amber-400";
    case "CANCELLED":
      return "border-l-soluud-border";
    case "PARTIALLY_CANCELLED":
      return "border-l-orange-400";
    default:
      return "border-l-soluud-border";
  }
}

function StatusPill({ status }: { status: DisplayStatus }) {
  const base =
    "flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium leading-tight";
  switch (status) {
    case "ACCEPTED":
      return (
        <span
          className={`${base} bg-soluud-secondary-light text-soluud-text-on-secondary`}
        >
          <span className="bg-soluud-secondary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Awaiting Payment</Trans>
        </span>
      );
    case "BOOKED":
      return (
        <span className={`${base} bg-soluud-primary/10 text-soluud-primary`}>
          <span className="bg-soluud-primary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Booked</Trans>
        </span>
      );
    case "CREATED":
      return (
        <span className={`${base} bg-amber-100 text-amber-700`}>
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
          <Trans>Pending</Trans>
        </span>
      );
    case "CANCELLED":
      return (
        <span
          className={`${base} bg-soluud-text-tertiary/10 text-soluud-text-tertiary`}
        >
          <span className="bg-soluud-text-tertiary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Cancelled</Trans>
        </span>
      );
    case "PARTIALLY_CANCELLED":
      return (
        <span className={`${base} bg-orange-50 text-orange-600`}>
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400" />
          <Trans>Partially Cancelled</Trans>
        </span>
      );
    default:
      return null;
  }
}

export default function UserBookingCard({ booking }: UserBookingCardProps) {
  const auth = useAuth();
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const [expanded, setExpanded] = useState(false);

  const displayStatus = getDisplayStatus(booking);
  const InstrumentIcon = getInstrumentIcon(booking.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    booking.instrument,
    language,
  );

  const activeSlots = booking.timeSlots
    .filter((s) => s.status !== "CANCELLED")
    .sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());
  const hasMore = activeSlots.length > VISIBLE_SLOTS;
  const visibleSlots = expanded
    ? activeSlots
    : activeSlots.slice(0, VISIBLE_SLOTS);
  const hiddenCount = activeSlots.length - VISIBLE_SLOTS;

  const userId = auth.user?.profile.sub;
  const isPast =
    activeSlots.length > 0 &&
    new Date(activeSlots[activeSlots.length - 1].to) < new Date();

  const canPay = booking.status === "ACCEPTED";
  const canMove =
    booking.status === "BOOKED" &&
    !!userId &&
    activeSlots.length > 0 &&
    !isPast;
  const canCancel =
    (booking.status === "BOOKED" || booking.status === "CREATED") &&
    activeSlots.length > 0 &&
    !isPast;
  const canLeaveFeedback = booking.status === "BOOKED" && isPast;

  const hasFooter = canPay || canMove || canCancel || canLeaveFeedback;

  return (
    <div
      className={`bg-soluud-background-card border-soluud-border flex flex-col gap-2.5 rounded-xl border border-l-4 px-4 py-3 shadow-[--shadow-card] transition-shadow duration-200 hover:shadow-[--shadow-card-hover] ${getLeftBorder(displayStatus)}`}
    >
      {/* Row 1: Instrument + created date + status pill */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <InstrumentIcon className="text-soluud-primary h-4 w-4 shrink-0" />
          <span className="text-soluud-text-primary truncate text-sm font-semibold">
            {instrumentLabel || booking.instrument}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {booking.createdAt && (
            <span className="text-soluud-text-tertiary flex items-center gap-1 text-xs">
              <ClockPlus className="h-3 w-3 shrink-0" />
              <TooltipText
                tooltip={t`Created at ${format(new Date(booking.createdAt), "Pp", { locale: dateFnsLocale })}`}
              >
                <span>
                  {format(new Date(booking.createdAt), "PP", {
                    locale: dateFnsLocale,
                  })}
                </span>
              </TooltipText>
            </span>
          )}
          <StatusPill status={displayStatus} />
        </div>
      </div>

      {/* Row 2: Teacher + location */}
      <div className="text-soluud-text-secondary flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5">
          <ProfilePicture
            profilePictureUrl={booking.teacherProfilePictureUrl}
            className="h-3.5 w-3.5 shrink-0"
            iconSize={10}
          />
          <Link
            to={`/profile/${booking.teacherId}`}
            className="hover:text-soluud-primary hover:underline"
          >
            {booking.teacherName}
          </Link>
        </span>
        <span className="text-soluud-text-tertiary hidden sm:inline">·</span>
        <span className="flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0 opacity-50" />
          <span className="truncate">{booking.locationName}</span>
        </span>
      </div>

      {/* Move request banner (only relevant for BOOKED) */}
      {userId && booking.status === "BOOKED" && (
        <ChangeRequestBanner bookingId={booking.id} currentUserId={userId} />
      )}

      {/* Row 3: Timeslots */}
      {activeSlots.length > 0 && (
        <div className="flex flex-col gap-1">
          {visibleSlots.map((slot) => (
            <div
              key={slot.id}
              className="text-soluud-text-secondary flex items-center gap-3 text-xs"
            >
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3 shrink-0 opacity-50" />
                <span>
                  {formatSmartDate(
                    slot.from,
                    dateFnsLocale,
                    t`Today`,
                    t`Tomorrow`,
                  )}
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-3 w-3 shrink-0 opacity-50" />
                <span>{formatTimeRange(slot, dateFnsLocale)}</span>
              </span>
            </div>
          ))}
          {hasMore && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="text-soluud-primary hover:text-soluud-primary/80 mt-0.5 flex items-center gap-1 self-start text-xs font-medium transition-colors"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  <Trans>Show less</Trans>
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />
                  <Trans>And {hiddenCount} more</Trans>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Row 4: Price */}
      <div className="flex items-center gap-1 text-xs">
        <Euro className="text-soluud-text-tertiary h-3 w-3 shrink-0" />
        <span className="text-soluud-text-primary font-semibold">
          {booking.price.toFixed(2)} €
        </span>
      </div>

      {/* Footer: Pay / Move / Cancel / Feedback */}
      {hasFooter && (
        <div className="border-soluud-border-subtle flex flex-wrap items-center justify-end gap-1 border-t pt-2.5">
          {canPay && (
            <Link
              to={`/checkout/${booking.id}`}
              className="bg-soluud-primary hover:bg-soluud-primary-hover flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium text-white transition-colors"
            >
              <CreditCard className="h-3.5 w-3.5 shrink-0" />
              <Trans>Pay Now</Trans>
            </Link>
          )}
          {canLeaveFeedback && (
            <Link
              to={`/teachers/${booking.teacherId}/feedback`}
              className="text-soluud-text-tertiary hover:bg-soluud-primary/5 hover:text-soluud-primary flex items-center gap-1 rounded-md px-2 py-1 text-xs whitespace-nowrap transition-colors duration-150"
            >
              <MessageSquareQuote className="h-3 w-3" />
              <Trans>Leave Feedback</Trans>
            </Link>
          )}
          {canMove && (
            <Link
              to={`/user/${userId}/bookings/${booking.id}/move`}
              className="text-soluud-text-tertiary hover:bg-soluud-primary/5 hover:text-soluud-primary flex items-center gap-1 rounded-md px-2 py-1 text-xs whitespace-nowrap transition-colors duration-150"
            >
              <ArrowRightLeft className="h-3 w-3" />
              <Trans>Move</Trans>
            </Link>
          )}
          {canCancel && (
            <Link
              to={`/user/${booking.bookerId}/bookings/${booking.id}/cancel`}
              className="text-soluud-text-tertiary hover:bg-soluud-warning/5 hover:text-soluud-warning flex items-center gap-1 rounded-md px-2 py-1 text-xs whitespace-nowrap transition-colors duration-150"
            >
              <Ban className="h-3 w-3" />
              <Trans>Cancel</Trans>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
